import { useState } from 'react';
import type { NoteVisibility } from '@hospasense/contracts';
import { ApiError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { useCreateNote, useNotes } from '../../lib/queries-s2.js';
import { formatDateTime } from '../../lib/format.js';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  ErrorState,
  LoadingState,
  Select,
  Textarea,
  useToast,
} from '../../components/ui/index.js';

const VISIBILITY_TONES = { internal: 'neutral', restricted: 'warning', security: 'error' } as const;

/** Notes tab: visibility classes mirror API read permissions exactly. */
export function NotesTab({ orgId }: { orgId: string }) {
  const { hasPermission } = useAuth();
  const canRead = hasPermission('note.read');
  const query = useNotes(orgId, canRead);
  const create = useCreateNote(orgId);
  const toast = useToast();
  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState<NoteVisibility>('internal');

  const writable: NoteVisibility[] = [
    ...(hasPermission('note.read') ? (['internal'] as const) : []),
    ...(hasPermission('note.read.restricted') ? (['restricted'] as const) : []),
    ...(hasPermission('note.read.security') ? (['security'] as const) : []),
  ];

  if (!canRead) return <EmptyState title="No access" description="Your role cannot view notes." />;
  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) {
    return (
      <ErrorState description="Notes could not be loaded." onRetry={() => void query.refetch()} />
    );
  }

  return (
    <div className="space-y-4">
      {hasPermission('note.write') && (
        <Card>
          <CardBody className="space-y-3 pt-4">
            <Textarea
              label="New note"
              hint="Business context only — clinical or credential content is rejected at the boundary."
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <div className="flex items-end justify-between gap-3">
              <Select
                label="Visibility"
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as NoteVisibility)}
                hint="Only classes your role can read are offered."
              >
                {writable.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </Select>
              <Button
                loading={create.isPending}
                disabled={body.trim().length < 3}
                onClick={() =>
                  void create
                    .mutateAsync({ body, visibility })
                    .then(() => {
                      toast.success('Note added');
                      setBody('');
                    })
                    .catch((err) =>
                      toast.error(err instanceof ApiError ? err.message : 'Note rejected'),
                    )
                }
              >
                Add note
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
      {query.data.items.length === 0 ? (
        <EmptyState
          title="No notes yet"
          description="Notes visible to your role would appear here."
        />
      ) : (
        <ul className="space-y-3">
          {query.data.items.map((note) => (
            <li key={note.id}>
              <Card>
                <CardBody className="space-y-1 pt-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-text-muted">
                      {note.authorDisplayName ?? 'Unknown'} · {formatDateTime(note.createdAt)}
                    </p>
                    <Badge tone={VISIBILITY_TONES[note.visibility]}>{note.visibility}</Badge>
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-text-primary">{note.body}</p>
                </CardBody>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
